// src/com/trans_plan_return/TransPlanReturnInput.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { enqueueSnackbar } from 'notistack';
import { TransPlanReturnDictionary } from './TransPlanReturnDict';
import { useMutation, useReactiveVar } from '@apollo/client';
import { authTableTreeRepo } from '../../value/AppModel';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import { SAVE_TRANS_PLAN_RETURN } from './TransPlanReturnGql';
import { ApiMsg, AppObject, AppStyle, AppWord } from '../../value/AppConst';
import InputUtil from '../../value/InputUtil';
import {
    Accordion, AccordionDetails, AccordionSummary, Button, Dialog, DialogActions, DialogContent, Grid2, Slide
} from '@mui/material';
import { DialogHeader } from '../../common/TopStyle';
import Datetime24Picker from '../../common/Datetime24Picker';
import ValueMisc from '../../value/ValueMisc';
import VzmIntField from '../../common/VzmIntField';
import VzmTextField from '../../common/VzmTextField';


const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const InactiveBg = '#777';
const AccordionHeader = styled(AccordionSummary)({
    //backgroundColor: AppStyle.PrimaryRGB, 
    color: 'white', //padding: '0.5rem', height: '2rem',
    minHeight: '2rem', maxHeight: '2rem',
});

const HeadGrid = styled(Grid2)({
    backgroundColor: '#eee', textAlign: 'center', padding: '0.5rem 0 0.5rem',
    fontSize: '0.8rem', borderTopLeftRadius: '0.5rem', borderTopRightRadius: '0.5rem',
});

const LabelLeft = styled(Grid2)({
    backgroundColor: '#eee', padding: '0.5rem 0 0.5rem', fontSize: '0.8rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
});

const ValueGrid = styled(Grid2)({
    padding: '0.5rem 0 0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
});

const InputMap = TransPlanReturnDictionary.inputMap;

const TprTab = {
    Return: 1,
    Loan: 2,
    ArrivalTemp: 3
};

const sxHeader = {'&.Mui-expanded': { minHeight: '2.4rem', height: '2.4rem'}};

/**
 * 물류집기 입력
 * 점포관리(ArrivalToStore.js)에서 사용하는 컴포넌트.
 * 점포의 반품정보를 보여주는 화면
 * DC 사용자와 점포 사용자의 동작이 약간 다름(TSR 발행 이동).
 * @param {*} param0 
 * @returns 
 */
export default function TransPlanReturnInput({
    open,
    onClose,
    item, // trans_plan_return.
    onCompleteMutation,
}) {
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const [tabNow, setTabNow] = useState(TprTab.Return);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const addAlert = useAlertSetter();
    const checkAuth = useAuthorizationCheck();

    // ##### GraphQL Mutation. 'onError' for prevent from printing error at cons-ole (Uncaught exception) ###
    const [saveTransPlanReturns, responseSave] = useMutation( SAVE_TRANS_PLAN_RETURN, {
		onCompleted: (data, option) => onCompleteSaveReturns(data, option), 
		onError: (error) => addAlert({title: '물류집기 정보 저장 오류', message: [error.message]})
	} );

    useEffect(()=>{
        if(open && item) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            inputSet.data = {...item};
            if(!inputSet.data.inputArrTime) inputSet.data.inputArrTime = ValueMisc.getYMD() + AppWord.HMS_TAIL;
            if(!inputSet.data.inputDptrTime) {
                if(item.inputArrTime) inputSet.data.inputDptrTime = item.inputArrTime;
                else inputSet.data.inputDptrTime = ValueMisc.getYMD() + AppWord.HMS_TAIL;
            }
            editing = true;
                
            setInput(inputSet);
            setIsEdit(editing);
            // setDcAndStore({dcName:item.dcName, storeName:item.storeName});
        }
    }, [open, item]);

    const onCompleteSaveReturns = (data, clientOption) => {
        if(data.transPlanReturnSave.ok) {
            if(data.transPlanReturnSave.detail !== ApiMsg.OK) {
                addAlert({
                    title: "물류집기 정보 저장",
                    message: [data.transPlanReturnSave.detail]
                });
            }
            else enqueueSnackbar("물류집기 정보가 저장되었습니다.", {variant:'success', autoHideDuration: 1500});
            onCompleteMutation();
        }
        else addAlert({title:'물류집기 정보 저장 오류', message:[data.transPlanReturnSave.message]});
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        // onRequestSave(param, isEdit);
        saveTransPlanReturns({
            variables: {
                transPlanReturn: param,
            },
        });
    };

    const askCancel = () => {
        const confirm = window.confirm('변경사항이 있습니다. 취소하시겠습니까?');
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    const renderAccordions = () => {
        return(
            <Box>
                <Accordion expanded={tabNow === TprTab.Return} onChange={(e, v)=>setTabNow(TprTab.Return)}
                    style={{margin:0}}
                >
                    <AccordionHeader sx={sxHeader}
                        style={{backgroundColor: tabNow === TprTab.Return ? AppStyle.PrimaryRGB : InactiveBg}}
                    >센터반납 입력</AccordionHeader>
                    <AccordionDetails style={{paddingTop: '1rem'}}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={6}>
                                <Datetime24Picker
                                    dict={InputMap.inputArrTime}
                                    value={input.data.inputArrTime}
                                    onChange={onChangeValue}
                                />
                            </Grid2>
                            <Grid2 size={6}>
                                <Datetime24Picker
                                    dict={InputMap.inputDptrTime}
                                    value={input.data.inputDptrTime}
                                    onChange={onChangeValue}
                                />
                            </Grid2>
                            <HeadGrid size={6}>Pallet</HeadGrid>
                            <HeadGrid size={6}>집기적재 Pallet</HeadGrid>
                            <Grid2 size={3}>
                                <VzmIntField
                                    dict={InputMap.dcReturnKpp}
                                    value={input.data.dcReturnKpp}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={3}>
                                <VzmIntField
                                    dict={InputMap.dcReturnAju}
                                    value={input.data.dcReturnAju}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={3}>
                                <VzmIntField
                                    dict={InputMap.dcReturnStackKpp}
                                    value={input.data.dcReturnStackKpp}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={3}>
                                <VzmIntField
                                    dict={InputMap.dcReturnStackAju}
                                    value={input.data.dcReturnStackAju}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <HeadGrid size={6}>Single Pick Tray</HeadGrid>
                            <HeadGrid size={6}>보냉커버</HeadGrid>
                            <Grid2 size={3}>
                                <VzmIntField
                                    dict={InputMap.dcReturnSpt}
                                    value={input.data.dcReturnSpt}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={3}>
                                <VzmIntField
                                    dict={InputMap.dcReturnSpt533}
                                    value={input.data.dcReturnSpt533}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={3}>
                                <VzmIntField
                                    dict={InputMap.dcReturnFull}
                                    value={input.data.dcReturnFull}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={3}>
                                <VzmIntField
                                    dict={InputMap.dcReturnHalf}
                                    value={input.data.dcReturnHalf}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={12} style={{fontSize:'0.3rem'}}>&nbsp;</Grid2>
                            <Grid2 size={2}>
                                <VzmIntField
                                    dict={InputMap.dcReturnNjc800}
                                    value={input.data.dcReturnNjc800}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={2}>
                                <VzmIntField
                                    dict={InputMap.dcReturnRollcage}
                                    value={input.data.dcReturnRollcage}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={2}>
                                <VzmIntField
                                    dict={InputMap.dcReturnDanpra}
                                    value={input.data.dcReturnDanpra}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={2}>
                                <VzmIntField
                                    dict={InputMap.dcReturnWooden}
                                    value={input.data.dcReturnWooden}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={2}>
                                <VzmIntField
                                    dict={InputMap.dcReturnOctagon}
                                    value={input.data.dcReturnOctagon}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={2}>
                                <VzmIntField
                                    dict={InputMap.dcReturnEtc}
                                    value={input.data.dcReturnEtc}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <LabelLeft size={3}>점포 인계확인</LabelLeft>
                            <ValueGrid size={3}>
                                {item.returnStoreCheckYn === 'Y' ? '예' : '아니오'}
                            </ValueGrid>
                            <LabelLeft size={3}>센터 반납확인</LabelLeft>
                            <ValueGrid size={3}>
                                {item.returnDcCheckUserName || '[없음]'}
                            </ValueGrid>
                            <Grid2 size={12}>
                                <VzmTextField
                                    dict={InputMap.dcReturnNote}
                                    value={input.data.dcReturnNote}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                    size={AppWord.SMALL}
                                />
                            </Grid2>
                        </Grid2>
                    </AccordionDetails>
                </Accordion>
                <Accordion expanded={tabNow === TprTab.Loan} onChange={(e, v)=>setTabNow(TprTab.Loan)}
                    style={{margin:0}}
                >
                    <AccordionHeader sx={sxHeader}
                        style={{backgroundColor: tabNow === TprTab.Loan ? AppStyle.PrimaryRGB : InactiveBg}}
                    >점간대출(RTV) 입력</AccordionHeader>
                    <AccordionDetails style={{paddingTop: '1rem'}}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={12}>
                                <VzmTextField
                                    dict={InputMap.dcCoverStores}
                                    value={input.data.dcCoverStores}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={4}>
                                <VzmTextField
                                    dict={InputMap.dcReturnSealNo}
                                    value={input.data.dcReturnSealNo}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={4}>
                                <VzmTextField
                                    dict={InputMap.dcReturnSealNo2}
                                    value={input.data.dcReturnSealNo2}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={4}>
                                <VzmTextField
                                    dict={InputMap.dcReturnSealNo3}
                                    value={input.data.dcReturnSealNo3}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={6}>
                                <VzmTextField
                                    dict={InputMap.dcCoverItem1}
                                    value={input.data.dcCoverItem1}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={6}>
                                <VzmTextField
                                    dict={InputMap.dcCoverItem2}
                                    value={input.data.dcCoverItem2}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                        </Grid2>

                    </AccordionDetails>
                </Accordion>
                <Accordion expanded={tabNow === TprTab.ArrivalTemp} onChange={(e, v)=>setTabNow(TprTab.ArrivalTemp)}
                    style={{margin:0}}
                >
                    <AccordionHeader sx={sxHeader}
                        style={{backgroundColor: tabNow === TprTab.ArrivalTemp ? AppStyle.PrimaryRGB : InactiveBg}}
                    >도착온도 입력</AccordionHeader>
                    <AccordionDetails style={{paddingTop: '1rem'}}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={6}>
                                <VzmTextField
                                    dict={InputMap.inputTempF}
                                    value={input.data.inputTempF}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={6}>&nbsp;</Grid2>
                            <Grid2 size={6}>
                                <VzmTextField
                                    dict={InputMap.inputTempM}
                                    value={input.data.inputTempM}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={6}>&nbsp;</Grid2>
                            <Grid2 size={6}>
                                <VzmTextField
                                    dict={InputMap.inputTempR}
                                    value={input.data.inputTempR}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid2>
                            <Grid2 size={6}>&nbsp;</Grid2>
                        </Grid2>

                    </AccordionDetails>
                </Accordion>
            </Box>
        );
    };

    if(!open || !item) return null;

    const canSave = checkAuth(authTableTree.tree.pr_trans_plan_return_save);

    return(
        <Dialog open={open} onClose={onClickCancel} fullScreen
            slots={{transition: Transition}}
        >
            <DialogHeader
                title="점포관리: 반납/대출/도착온도"
                onClickClose={onClickCancel}
            />
            <DialogContent style={{padding: '1rem 0.2rem', }}>
                {renderAccordions()}
            </DialogContent>
            <DialogActions>
                <Button
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.primary}
                    onClick={onClickSubmit}
                    disabled={responseSave.loading || !canSave || !input.changed}
                >저장</Button>
            </DialogActions>
        </Dialog>
    );
}